// api.ts
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

const ID_TOKEN_KEY = "id_token";
const REFRESH_TOKEN_KEY = "refresh_token";

// @ts-ignore
const BASE_URL: string = (import.meta.env?.VITE_API_BASE_URL as string) ?? "/";

export const authBus = new EventTarget(); // emits: auth:refreshed, auth:failed

// Main client (uses interceptors)
const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 30_000,
});

// Raw client (no interceptors) to avoid recursion on refresh
const refreshClient = axios.create({
    baseURL: BASE_URL,
    timeout: 30_000,
});

// In-memory cache for quick header injection
let idToken: string | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
// Incremented on every setTokens/clearTokens call so stale in-flight refreshes
// can detect that a new session has started and abandon their side-effects.
let sessionNonce = 0;

/* ───────────────────────────── storage helpers ───────────────────────────── */
function getStoredIdToken() {
    try {
        return localStorage.getItem(ID_TOKEN_KEY);
    } catch {
        return null;
    }
}
function getStoredRefreshToken() {
    try {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
        return null;
    }
}
function setStoredTokens(tokens: { idToken: string | null; refreshToken?: string | null }) {
    try {
        if (tokens.idToken) localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
        else localStorage.removeItem(ID_TOKEN_KEY);

        if (tokens.refreshToken !== undefined) {
            if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            else localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
    } catch {
        // no-op (Safari private mode etc.)
    }
}

/* ───────────────────────────── session teardown ───────────────────────────── */
// Clears the session and tells the app to route to sign-in. AuthContext listens for
// auth:failed and drops its user state, so this is the single way out of a session
// that cannot be recovered.
function endSession(reason: string) {
    console.warn(`[auth] session ended (${reason})`);
    idToken = null;
    setStoredTokens({ idToken: null, refreshToken: null });
    authBus.dispatchEvent(new CustomEvent("auth:failed"));
}

/* ───────────────────────────── token refresh ───────────────────────────── */
async function refreshIdToken(): Promise<string | null> {
    // Capture the nonce up front so EVERY exit path (including the no-refresh-token
    // teardown below) can tell whether a newer login/logout has superseded this
    // refresh before we mutate any state or dispatch auth:failed. Without this, a
    // stale caller that transiently reads a null refresh token mid-login could clear
    // a freshly-established session.
    const captured = sessionNonce;

    // Hard-logout teardown shared by every unrecoverable exit: clear tokens and
    // signal a logout, but only if this refresh still owns the current session.
    const failSession = (reason: string): null => {
        if (sessionNonce !== captured) return null;
        endSession(`token refresh failed: ${reason}`);
        return null;
    };

    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
        // No refresh token means the session can't be recovered — don't sit in a
        // zombie authenticated state (id_token present, but every request 401s).
        return failSession("no refresh_token");
    }

    try {
        const { data } = await refreshClient.post("/api/v1/auth/refresh", { refreshToken });
        const newId: string | null = data?.idToken ?? null;
        const newRef: string | null = data?.refreshToken ?? null;

        if (sessionNonce !== captured) return null; // new login/logout supersedes this refresh

        // A 200 with no id_token is not a usable session — treat it as a failure
        // rather than nulling the token while reporting success.
        if (!newId) return failSession("refresh response missing id_token");

        idToken = newId;
        setStoredTokens({ idToken: newId, refreshToken: newRef ?? undefined });

        authBus.dispatchEvent(new CustomEvent("auth:refreshed"));
        return newId;
    } catch {
        return failSession("refresh request rejected");
    }
}

// Single-flight guard shared by the response interceptor AND the app's mount-time
// refresh. Concurrent callers await the same in-flight refresh so a rotating refresh
// token is never consumed twice.
function runSingleFlightRefresh(): Promise<string | null> {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshIdToken().finally(() => {
            isRefreshing = false;
        });
    }
    return refreshPromise as Promise<string | null>;
}

/* ───────────────────────────── request interceptor ───────────────────────────── */
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const tok = idToken ?? getStoredIdToken();
    if (tok) {
        // Axios 1 headers can be AxiosHeaders or plain object
        if (config.headers instanceof AxiosHeaders) {
            (config.headers as AxiosHeaders).set("Authorization", `Bearer ${tok}`);
        } else {
            // @ts-ignore
            config.headers = { ...(config.headers || {}), Authorization: `Bearer ${tok}` };
        }
    }
    return config;
});

/* ───────────────────────────── response interceptor ──────────────────────────── */
// _retry marks a request that has already been refreshed and re-issued once.
// _sessionNonce records which session that retry belonged to, so a 401 arriving
// after a login or logout cannot tear down the session that replaced it.
type RetriableConfig = AxiosRequestConfig & { _retry?: boolean; _sessionNonce?: number };

const isAuthPath = (url?: string) =>
    !!url && (/\/auth\/login/i.test(url) || /\/auth\/refresh/i.test(url));

instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
        const status = error.response?.status ?? 0;
        const original = error.config as RetriableConfig | undefined;

        // Only handle 401s on non-auth endpoints with a retriable request
        if (!original || status !== 401 || isAuthPath(original.url || "")) {
            // Not our case → bubble up
            return Promise.reject(error);
        }

        // A 401 on a request that already carried a freshly refreshed token means the
        // identity is being turned away, not the token: the backend now rejects
        // disabled and soft-deleted users. Refreshing again would keep succeeding
        // while every call keeps failing, so the session is over. Skipped when a
        // login or logout has happened since the retry, because this 401 then
        // belongs to a session that is already gone.
        if (original._retry) {
            if (original._sessionNonce === sessionNonce) {
                endSession("401 after a successful refresh");
            }
            return Promise.reject(error);
        }

        original._retry = true;
        original._sessionNonce = sessionNonce;

        try {
            // Ensure a single refresh in-flight for all 401 waiters
            const newToken = await runSingleFlightRefresh();
            if (!newToken) {
                // The refresh could not be completed, so refreshIdToken has already
                // ended the session. Bubble up so the caller can surface the failure.
                return Promise.reject(error);
            }

            // Re-issue original request with fresh Authorization header
            original.headers = original.headers ?? {};
            if (original.headers instanceof AxiosHeaders) {
                (original.headers as AxiosHeaders).set("Authorization", `Bearer ${newToken}`);
            } else {
                original.headers = { ...(original.headers as any), Authorization: `Bearer ${newToken}` };
            }

            return instance.request(original);
        } catch (e) {
            return Promise.reject(e);
        }
    }
);

/* ───────────────────────────── public API ───────────────────────────── */
const api = {
    get: (...args: Parameters<typeof instance.get>) => instance.get(...args),
    post: (...args: Parameters<typeof instance.post>) => instance.post(...args),
    put: (...args: Parameters<typeof instance.put>) => instance.put(...args),
    patch: (...args: Parameters<typeof instance.patch>) => instance.patch(...args),
    delete: (...args: Parameters<typeof instance.delete>) => instance.delete(...args),
    request: (...args: Parameters<typeof instance.request>) => instance.request(...args),

    instance,

    setTokens(idTok: string | null, refreshTok: string | null) {
        sessionNonce++; // invalidate any in-flight refresh
        // Drop any stale single-flight refresh so it can't bleed into this session.
        // Required for correctness: without it, a slow refresh still in flight during
        // an in-app logout→login would be joined by a post-login 401 waiter, resolve to
        // null via the nonce guard, and wrongly log the valid new session out.
        isRefreshing = false;
        refreshPromise = null;
        idToken = idTok;
        setStoredTokens({ idToken: idTok, refreshToken: refreshTok });

        // also set defaults (extra safety)
        if (idTok) instance.defaults.headers.common["Authorization"] = `Bearer ${idTok}`;
        else delete instance.defaults.headers.common["Authorization"];
    },

    clearTokens() {
        sessionNonce++; // invalidate any in-flight refresh
        isRefreshing = false;
        refreshPromise = null;
        idToken = null;
        setStoredTokens({ idToken: null, refreshToken: null });
        delete instance.defaults.headers.common["Authorization"];
    },

    // Single source of truth for token refresh, shared with the response interceptor.
    // Reads the refresh token from storage, updates the in-memory + stored id_token on
    // success, and dispatches auth:failed on unrecoverable failure. Returns the new
    // id_token, or null if the refresh could not be completed.
    refreshIdToken(): Promise<string | null> {
        return runSingleFlightRefresh();
    },
};

export default api;
